import { DIOPTRA_AUTHOR } from "@common/utils/redline/redline-engine";
import { NO_TABLE_CHANGES } from "@common/utils/redline/table-operations";
import type { ChangeThroughoutMatch } from "@common/utils/redline/wire/change-throughout";
import { type CardParagraphAnchor, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";

import type { AttachedRedline } from "../../../utils/anchor/anchor-tracker";
import { anchorTrackers, documentPages } from "../../../utils/anchor/anchor-tracker-registry";
import type { RecoveryAttempt } from "../../../utils/anchor/recovery/recovery-chain";
import { commentTarget } from "../../../utils/comment-anchors";
import { selectCommentRange } from "../../../utils/comment-reply";
import { withWordContextWrite } from "../../../utils/document";
import { selectSlotPlaceInWord, type SlotPlace } from "../../../utils/redline-insertion-slot";
import { toastError } from "../../../utils/toast";
import type { ProposalCardContent } from "./card-content";

const REPLY_TARGET_MISSING_MESSAGE = "We can't find the comment this replies to. It may have been deleted or resolved.";
export const PLACE_NOT_FOUND_MESSAGE = "Changes may have altered this location, we are not able to find it.";
export const PLACE_CONFLICTED_MESSAGE = "More than one place in the document fits this change";

function placeNamedByCandidate(candidate: RecoveryAttempt["candidates"][number]): string {
  return candidate.anchorPhrase ? `“${candidate.anchorPhrase}”` : `paragraph ${candidate.paragraphIndex + 1}`;
}

function placeConflictedMessage(attempt: RecoveryAttempt): string {
  const places = attempt.candidates.map(placeNamedByCandidate);
  const named = places.length > 0 ? `${PLACE_CONFLICTED_MESSAGE}: ${places.join("; ")}.` : `${PLACE_CONFLICTED_MESSAGE}.`;
  return `${named} ${attempt.explanation ?? ""}`.trim();
}

export type SelectBookmarksRangeIfPresent = (bookmarks: string[]) => Promise<boolean>;

export type SlotPlaceOfCard = (anchorBookmark: string) => Promise<SlotPlace>;

export function bookmarksLocatingProposalCard(content: ProposalCardContent): string[] {
  if (content.kind === "reply") return [];
  if (content.kind === "paragraph") return [content.precedingParagraphBookmark];
  return content.bookmarks;
}

export function paragraphAsItReads(bookmark: string, text: string, anchor: CardParagraphAnchor | null = null): AttachedRedline {
  return {
    kind: "texts",
    author: DIOPTRA_AUTHOR,
    paragraphs: [{ bookmark, textWhenProposed: text, textProposed: text, anchor }],
    insertions: [],
    tableChanges: NO_TABLE_CHANGES,
    cardKind: "paragraphs",
    reverts: [],
  };
}

export function redlineLocatingProposalCard(content: ProposalCardContent): AttachedRedline | null {
  if (content.kind === "reply") return null;
  if (content.kind === "comment") {
    const [bookmark] = content.bookmarks;
    return bookmark === undefined || content.paragraphText === null ? null : paragraphAsItReads(bookmark, content.paragraphText, content.paragraphAnchor);
  }
  if (content.textRedline !== null) return textsPayloadFrom(content.textRedline);
  if (content.kind === "paragraph") {
    return content.precedingParagraphText === null ? null : paragraphAsItReads(content.precedingParagraphBookmark, content.precedingParagraphText);
  }
  return content.markup === null ? null : { kind: "ooxml", redlineOoxml: content.markup, author: DIOPTRA_AUTHOR };
}

async function refindAttempt(bookmarks: string[], redline: AttachedRedline | null): Promise<RecoveryAttempt | null> {
  try {
    return await anchorTrackers.trackerFor(bookmarks, redline).refind();
  } catch {
    return null;
  }
}

export async function goToDocumentPlace(bookmarks: string[], redline: AttachedRedline | null, selectBookmarksRangeIfPresent: SelectBookmarksRangeIfPresent): Promise<void> {
  if (await selectBookmarksRangeIfPresent(bookmarks)) {
    documentPages.touch();
    return;
  }
  const attempt = await refindAttempt(bookmarks, redline);
  if (attempt?.outcome === "recovered" && await selectBookmarksRangeIfPresent(bookmarks)) {
    documentPages.touch();
    return;
  }
  toastError(attempt?.outcome === "ambiguous" ? placeConflictedMessage(attempt) : PLACE_NOT_FOUND_MESSAGE);
}

async function selectedInItsSlot(anchorBookmark: string, slotPlaceOfCard: SlotPlaceOfCard): Promise<boolean> {
  const place = await slotPlaceOfCard(anchorBookmark);
  return place.kind === "blocks" && selectSlotPlaceInWord(place);
}

export function newParagraphIdsOf(content: ProposalCardContent): string[] {
  if (content.kind === "reply" || content.kind === "comment" || content.textRedline === null) return [];
  return content.textRedline.insertions.flatMap((insertion) => (insertion.bookmark === undefined || insertion.bookmark === null ? [] : [insertion.bookmark]));
}

async function selectedByItsNewParagraphIds(content: ProposalCardContent, selectBookmarksRangeIfPresent: SelectBookmarksRangeIfPresent): Promise<boolean> {
  const newParagraphIds = newParagraphIdsOf(content);
  return newParagraphIds.length > 0 && selectBookmarksRangeIfPresent(newParagraphIds);
}

function placeQuoteOf(place: ChangeThroughoutMatch): string {
  if (place.text_before.length > 0) return `${place.text_before}${place.text}`;
  return `${place.text}${place.text_after}`;
}

function placeAmong(place: ChangeThroughoutMatch, occurrencesInQuote: Word.Range[]): Word.Range | undefined {
  if (place.text_before.length > 0) return occurrencesInQuote[occurrencesInQuote.length - 1];
  return occurrencesInQuote[0];
}

async function selectedInItsParagraph(place: ChangeThroughoutMatch): Promise<boolean> {
  return withWordContextWrite(async (context) => {
    const paragraphRange = context.document.getBookmarkRangeOrNullObject(place.paragraph_id);
    paragraphRange.load("isNullObject");
    await context.sync();
    if (paragraphRange.isNullObject) return false;
    const quoteRange = (await commentTarget({ bookmarkedRange: paragraphRange, placement: "span", anchorText: placeQuoteOf(place) })).range;
    const occurrencesInQuote = quoteRange.search(place.text, { matchCase: true });
    occurrencesInQuote.load("items");
    await context.sync();
    (placeAmong(place, occurrencesInQuote.items) ?? quoteRange).select();
    await context.sync();
    return true;
  });
}

export async function locateChangeThroughoutPlace(place: ChangeThroughoutMatch): Promise<void> {
  try {
    if (!(await selectedInItsParagraph(place))) {
      toastError(PLACE_NOT_FOUND_MESSAGE);
      return;
    }
    documentPages.touch();
  } catch (error) {
    toastError(error);
  }
}

export async function locateProposalCard(content: ProposalCardContent, selectBookmarksRangeIfPresent: SelectBookmarksRangeIfPresent, slotPlaceOfCard: SlotPlaceOfCard): Promise<void> {
  if (content.kind === "reply") {
    const targetCommentFound = await withWordContextWrite((context) => selectCommentRange(context, content.targetCommentId));
    if (!targetCommentFound) {
      toastError(REPLY_TARGET_MISSING_MESSAGE);
      return;
    }
    documentPages.touch();
    return;
  }
  if (content.kind === "paragraph" && (await selectedByItsNewParagraphIds(content, selectBookmarksRangeIfPresent) || await selectedInItsSlot(content.precedingParagraphBookmark, slotPlaceOfCard))) {
    documentPages.touch();
    return;
  }
  const bookmarks = bookmarksLocatingProposalCard(content);
  if (bookmarks.length > 0) await goToDocumentPlace(bookmarks, redlineLocatingProposalCard(content), selectBookmarksRangeIfPresent);
}
